// components/TemplatesEditor.tsx
// Editor de plantillas de mensajes (pivot migración 011).
// Se usa dentro de ProfessionalNotificationConfig.

import React, { useEffect, useState } from 'react';
import {
  MessageSquare, Plus, Save, Trash2, Star, StarOff,
  Loader2, AlertCircle, Copy, Info
} from 'lucide-react';
import {
  getTemplates, createTemplate, updateTemplate, deleteTemplate,
  MessageTemplate
} from '../services/reminders.api';

interface TemplatesEditorProps {
  professionalId: number;
}

const PLACEHOLDERS = [
  '{paciente}', '{profesional}', '{fecha}', '{fecha_corta}', '{hora}',
  '{servicio}', '{direccion}', '{duracion}', '{precio}', '{notas}',
];

const TemplatesEditor: React.FC<TemplatesEditorProps> = ({ professionalId }) => {
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<number | 'new' | null>(null);
  // Edición local por id
  const [drafts, setDrafts] = useState<Record<number, { title: string; body: string }>>({});
  // Formulario para nueva
  const [showNew, setShowNew] = useState(false);
  const [newTpl, setNewTpl] = useState({ template_key: '', title: '', body: '' });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTemplates(professionalId);
      setTemplates(data);
      const d: Record<number, { title: string; body: string }> = {};
      data.forEach((t) => { d[t.id] = { title: t.title, body: t.body }; });
      setDrafts(d);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (professionalId) load(); /* eslint-disable-next-line */ }, [professionalId]);

  const handleSave = async (t: MessageTemplate) => {
    const draft = drafts[t.id];
    if (!draft) return;
    setSavingId(t.id);
    try {
      const updated = await updateTemplate(professionalId, t.id, {
        title: draft.title,
        body: draft.body,
      });
      setTemplates((prev) => prev.map((x) => (x.id === t.id ? updated : x)));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSavingId(null);
    }
  };

  const handleSetDefault = async (t: MessageTemplate) => {
    setSavingId(t.id);
    try {
      await updateTemplate(professionalId, t.id, { is_default: true });
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (t: MessageTemplate) => {
    if (!window.confirm(`¿Eliminar la plantilla "${t.title}"?`)) return;
    setSavingId(t.id);
    try {
      await deleteTemplate(professionalId, t.id);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSavingId(null);
    }
  };

  const handleCreate = async () => {
    if (!newTpl.template_key.trim() || !newTpl.title.trim() || !newTpl.body.trim()) {
      setError('Todos los campos son requeridos para crear una plantilla');
      return;
    }
    setSavingId('new');
    setError(null);
    try {
      await createTemplate(professionalId, {
        template_key: newTpl.template_key.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'),
        title: newTpl.title.trim(),
        body: newTpl.body,
      });
      setNewTpl({ template_key: '', title: '', body: '' });
      setShowNew(false);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSavingId(null);
    }
  };

  const insertPlaceholder = (id: number, ph: string) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: { ...prev[id], body: (prev[id]?.body || '') + ph },
    }));
  };

  const insertPlaceholderNew = (ph: string) => {
    setNewTpl((prev) => ({ ...prev, body: prev.body + ph }));
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <MessageSquare className="w-5 h-5 text-emerald-600" />
            Plantillas de mensajes
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            La secretaria usa estas plantillas al enviar recordatorios por WhatsApp.
          </p>
        </div>
        {!showNew && (
          <button
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-600 text-white hover:bg-emerald-700 rounded-md"
          >
            <Plus className="w-4 h-4" /> Nueva
          </button>
        )}
      </div>

      {/* Placeholders helper */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <div className="flex items-start gap-2 text-xs text-blue-900">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Variables disponibles</strong> (se reemplazan al enviar):
            <div className="mt-1 flex flex-wrap gap-1">
              {PLACEHOLDERS.map((p) => (
                <code
                  key={p}
                  className="px-1.5 py-0.5 bg-white border border-blue-200 rounded text-blue-800"
                >
                  {p}
                </code>
              ))}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}

      {/* Nueva plantilla */}
      {showNew && (
        <div className="border-2 border-emerald-300 border-dashed rounded-lg p-4 mb-4 bg-emerald-50/30">
          <div className="grid gap-2 md:grid-cols-2 mb-2">
            <input
              type="text"
              placeholder="Clave única (ej: reminder_2h)"
              value={newTpl.template_key}
              onChange={(e) => setNewTpl({ ...newTpl, template_key: e.target.value })}
              className="px-3 py-2 text-sm border rounded-md"
            />
            <input
              type="text"
              placeholder="Título visible (ej: Recordatorio 2hs antes)"
              value={newTpl.title}
              onChange={(e) => setNewTpl({ ...newTpl, title: e.target.value })}
              className="px-3 py-2 text-sm border rounded-md"
            />
          </div>
          <textarea
            placeholder="Mensaje. Usá las variables de arriba, ej: Hola {paciente}, tu turno es el {fecha} a las {hora}..."
            value={newTpl.body}
            onChange={(e) => setNewTpl({ ...newTpl, body: e.target.value })}
            rows={4}
            className="w-full px-3 py-2 text-sm border rounded-md mb-2"
          />
          <div className="flex flex-wrap gap-1 mb-3">
            {PLACEHOLDERS.slice(0, 6).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => insertPlaceholderNew(p)}
                className="px-2 py-0.5 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                + {p}
              </button>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => { setShowNew(false); setNewTpl({ template_key: '', title: '', body: '' }); }}
              className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Cancelar
            </button>
            <button
              onClick={handleCreate}
              disabled={savingId === 'new'}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-600 text-white hover:bg-emerald-700 rounded-md disabled:opacity-50"
            >
              {savingId === 'new' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Crear plantilla
            </button>
          </div>
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div className="flex items-center justify-center py-8 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-8 text-gray-500 text-sm">
          No hay plantillas todavía. Creá la primera con el botón "Nueva".
        </div>
      ) : (
        <div className="space-y-4">
          {templates.map((t) => {
            const draft = drafts[t.id] || { title: t.title, body: t.body };
            const dirty = draft.title !== t.title || draft.body !== t.body;
            return (
              <div
                key={t.id}
                className={`border rounded-lg p-3 transition-colors ${
                  t.is_default ? 'border-amber-300 bg-amber-50/40' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between mb-2 gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {t.is_default && (
                      <Star className="w-4 h-4 text-amber-500 fill-amber-400 flex-shrink-0" />
                    )}
                    <input
                      type="text"
                      value={draft.title}
                      onChange={(e) =>
                        setDrafts((prev) => ({ ...prev, [t.id]: { ...draft, title: e.target.value } }))
                      }
                      className="flex-1 min-w-0 px-2 py-1 text-sm font-medium border border-transparent hover:border-gray-300 focus:border-emerald-500 focus:bg-white rounded focus:outline-none"
                    />
                    <code className="text-xs text-gray-400 flex-shrink-0">{t.template_key}</code>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!t.is_default && (
                      <button
                        onClick={() => handleSetDefault(t)}
                        disabled={savingId === t.id}
                        className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded"
                        title="Hacer default"
                      >
                        <StarOff className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(t)}
                      disabled={savingId === t.id}
                      className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <textarea
                  value={draft.body}
                  onChange={(e) =>
                    setDrafts((prev) => ({ ...prev, [t.id]: { ...draft, body: e.target.value } }))
                  }
                  rows={4}
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 focus:border-emerald-500 rounded resize-y font-mono"
                />
                <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                  <div className="flex flex-wrap gap-1">
                    {PLACEHOLDERS.slice(0, 6).map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => insertPlaceholder(t.id, p)}
                        className="px-1.5 py-0.5 text-[10px] bg-gray-100 hover:bg-gray-200 rounded"
                      >
                        + {p}
                      </button>
                    ))}
                  </div>
                  {dirty && (
                    <button
                      onClick={() => handleSave(t)}
                      disabled={savingId === t.id}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-emerald-600 text-white hover:bg-emerald-700 rounded-md disabled:opacity-50"
                    >
                      {savingId === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                      Guardar
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TemplatesEditor;
